import React, { useCallback, useState } from 'react';
import { Heart, Activity, Save, RefreshCw, Download, User, Award, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BaseParametersSection from './BaseParametersSection';
import HealthConditionSection from './HealthConditionSection';
import CalculationMethodSection from './CalculationMethodSection';
import ExerciseTypeSection from './ExerciseTypeSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета целевого пульса
 */
const CalculationForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  setIsCalculating,
  results,
  setResults,
  maxHRMethods,
  exerciseTypes,
  fitnessLevels,
  healthConditions,
  saveProfile
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Если отключили использование пульса в покое, удаляем соответствующее значение
      if (name === 'includeRestingHR' && !checked) {
        newState.restingHeartRate = '';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      age, gender, restingHeartRate, fitnessLevel, 
      exerciseType, healthCondition, calculationMethod, includeRestingHR
    } = formData;

    // Парсинг числовых значений
    const numAge = parseIntOrDefault(age, -1);
    const numRestingHR = parseIntOrDefault(restingHeartRate, -1);

    // Валидация базовых полей
    if (numAge <= 0) newErrors.age = 'Возраст должен быть положительным числом.';
    if (!newErrors.age && numAge > 120) newErrors.age = 'Максимальный возраст 120 лет.';
    
    if (includeRestingHR) {
      if (numRestingHR <= 0) newErrors.restingHeartRate = 'Пульс в покое должен быть положительным числом.';
      if (!newErrors.restingHeartRate && numRestingHR > 150) newErrors.restingHeartRate = 'Пульс в покое обычно не превышает 150 уд/мин.';
      if (!newErrors.restingHeartRate && numRestingHR < 30) newErrors.restingHeartRate = 'Пульс в покое обычно не ниже 30 уд/мин.';
    }

    if (!gender || !['male', 'female'].includes(gender)) {
      newErrors.gender = 'Выберите пол.';
    }

    if (!fitnessLevel) newErrors.fitnessLevel = 'Выберите уровень физической подготовки.';
    if (!exerciseType) newErrors.exerciseType = 'Выберите тип тренировки.';
    if (!healthCondition) newErrors.healthCondition = 'Выберите состояние здоровья.';
    if (!calculationMethod) newErrors.calculationMethod = 'Выберите метод расчета.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета целевой зоны пульса
  const calculateTargetHeartRate = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const age = parseIntOrDefault(formData.age);
        const gender = formData.gender;
        const restingHR = formData.includeRestingHR ? parseIntOrDefault(formData.restingHeartRate) : null;
        const selectedMethod = formData.calculationMethod;
        const exerciseType = formData.exerciseType;
        const fitnessLevel = formData.fitnessLevel;
        const healthCondition = formData.healthCondition;
        
        // Находим соответствующие объекты настроек
        const exerciseTypeObj = exerciseTypes.find(type => type.id === exerciseType) || exerciseTypes[0];
        const fitnessLevelObj = fitnessLevels.find(level => level.id === fitnessLevel) || fitnessLevels[1];
        const healthConditionObj = healthConditions.find(condition => condition.id === healthCondition) || healthConditions[0];
        const selectedMethodObj = maxHRMethods.find(method => method.id === selectedMethod);
        
        // Настройки из объектов для расчетов
        const defaultLowerPercent = exerciseTypeObj.defaultZones.lower;
        const defaultUpperPercent = exerciseTypeObj.defaultZones.upper;
        const methodDescription = selectedMethodObj ? selectedMethodObj.description : '';
        
        // Корректировки для расчёта
        const fitnessAdjustment = fitnessLevelObj ? fitnessLevelObj.adjustment : 0;
        const healthAdjustment = healthConditionObj ? healthConditionObj.adjustment : 0;
        const genderAdjustment = gender === 'female' ? 5 : 0;
        
        // --- Расчёт максимального пульса ---
        let maxHR;
        let maxHRFormula;
        
        switch (selectedMethod) {
          case 'tanaka':
            maxHR = 208 - 0.7 * age;
            maxHRFormula = '208 - 0.7 × возраст';
            break;
          case 'hf':
            maxHR = 220 - age;
            maxHRFormula = '220 - возраст';
            break;
          case 'nes':
            maxHR = 211 - 0.64 * age;
            maxHRFormula = '211 - 0.64 × возраст';
            break;
          case 'gellish':
            maxHR = 207 - 0.7 * age;
            maxHRFormula = '207 - 0.7 × возраст';
            break;
          default:
            maxHR = 208 - 0.7 * age;
            maxHRFormula = '208 - 0.7 × возраст (Танака)';
        }
        
        // Применяем корректировки
        maxHR += genderAdjustment + fitnessAdjustment + healthAdjustment;
        
        // Ограничиваем разумными пределами
        maxHR = Math.max(Math.min(maxHR, 230), 150);
        const roundedMaxHR = Math.round(maxHR);
        
        // Функции для расчёта пульса разными методами
        const calculateDirect = (percent) => Math.round(roundedMaxHR * (percent / 100));
        const calculateKarvonen = (percent) => {
          if (restingHR && formData.includeRestingHR) {
            return Math.round(((roundedMaxHR - restingHR) * (percent / 100)) + restingHR);
          }
          return calculateDirect(percent);
        };
        
        // Рассчитываем целевую зону
        const targetZone = {
          lowerPercent: defaultLowerPercent,
          upperPercent: defaultUpperPercent,
          lowerHR: calculateKarvonen(defaultLowerPercent),
          upperHR: calculateKarvonen(defaultUpperPercent)
        };
        
        // Рассчитываем все пульсовые зоны
        const zones = [
          {
            name: 'Зона восстановления',
            description: 'Очень легкая активность, разминка, восстановление',
            lowerPercent: 50,
            upperPercent: 60,
            lowerHR: calculateKarvonen(50),
            upperHR: calculateKarvonen(60)
          },
          {
            name: 'Зона жиросжигания',
            description: 'Легкая активность, улучшение базовой выносливости и сжигание жира',
            lowerPercent: 60,
            upperPercent: 70,
            lowerHR: calculateKarvonen(60),
            upperHR: calculateKarvonen(70)
          },
          {
            name: 'Аэробная зона',
            description: 'Умеренная активность, повышение аэробной выносливости',
            lowerPercent: 70,
            upperPercent: 80,
            lowerHR: calculateKarvonen(70),
            upperHR: calculateKarvonen(80)
          },
          {
            name: 'Анаэробная зона',
            description: 'Тяжелая активность, увеличение скорости и мощности',
            lowerPercent: 80,
            upperPercent: 90,
            lowerHR: calculateKarvonen(80),
            upperHR: calculateKarvonen(90)
          },
          {
            name: 'Максимальная зона',
            description: 'Максимальная нагрузка, только для подготовленных атлетов',
            lowerPercent: 90,
            upperPercent: 100,
            lowerHR: calculateKarvonen(90),
            upperHR: calculateKarvonen(100)
          }
        ];
        
        // Формируем итоговый результат
        const finalResults = {
          maxHeartRate: roundedMaxHR,
          maxHRFormula,
          methodDescription,
          restingHeartRate: restingHR,
          targetZone,
          zones,
          exerciseType: exerciseTypeObj.name,
          fitnessLevel: fitnessLevelObj.name,
          fitnessAdjustment,
          healthCondition: healthConditionObj.name,
          healthAdjustment,
          genderAdjustment,
          useKarvonen: formData.includeRestingHR && restingHR,
          karvonenFormula: 'THR = ((HRmax - HRrest) × Intensity%) + HRrest',
          directFormula: 'THR = HRmax × Intensity%',
          age,
          gender
        };
        
        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, exerciseTypes, fitnessLevels, healthConditions, maxHRMethods, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      age: '30',
      gender: 'male',
      restingHeartRate: '70',
      fitnessLevel: 'average',
      exerciseType: 'cardio',
      healthCondition: 'healthy',
      calculationMethod: 'tanaka',
      includeRestingHR: true,
    });
    setResults(null);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
  };

  // Экспорт зон пульса в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const formatCsvNumber = (num) => num.toString().replace('.', ',');

    // Заголовки
    const headers = ['Зона', 'Нижняя граница (%)', 'Верхняя граница (%)', 'Нижняя граница (уд/мин)', 'Верхняя граница (уд/мин)', 'Описание'];
    
    // Строки для каждой зоны
    const rows = results.zones.map(zone => {
      return [
        zone.name,
        formatCsvNumber(zone.lowerPercent),
        formatCsvNumber(zone.upperPercent),
        formatCsvNumber(zone.lowerHR),
        formatCsvNumber(zone.upperHR),
        zone.description
      ].join(';');
    });

    // Пустая строка для разделения
    const emptyRow = [''];

    // Основная информация
    const infoRows = [
      ['Максимальный пульс:', formatCsvNumber(results.maxHeartRate), 'уд/мин'],
      ['Формула расчета:', results.maxHRFormula],
      ['Возраст:', formatCsvNumber(results.age), 'лет'],
      ['Пол:', results.gender === 'male' ? 'мужской' : 'женский'],
      results.restingHeartRate ? ['Пульс в покое:', formatCsvNumber(results.restingHeartRate), 'уд/мин'] : emptyRow,
      ['Физическая подготовка:', results.fitnessLevel],
      ['Состояние здоровья:', results.healthCondition],
      ['Тип тренировки:', results.exerciseType],
      emptyRow,
      ['Рекомендуемая целевая зона:'],
      ['', formatCsvNumber(results.targetZone.lowerPercent) + '%', '-', formatCsvNumber(results.targetZone.upperPercent) + '%'],
      ['', formatCsvNumber(results.targetZone.lowerHR) + ' уд/мин', '-', formatCsvNumber(results.targetZone.upperHR) + ' уд/мин'],
      emptyRow
    ].map(row => row.join(';'));
    
    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'целевые_зоны_пульса.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTargetHeartRate();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-red-500" />
          Параметры расчета целевого пульса
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры */}
        <BaseParametersSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Уровень физической подготовки и тип тренировки */}
        <ExerciseTypeSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
          exerciseTypes={exerciseTypes}
          fitnessLevels={fitnessLevels}
        />

        {/* Состояние здоровья */}
        <HealthConditionSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
          healthConditions={healthConditions}
        />

        {/* Метод расчета */}
        <CalculationMethodSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
          maxHRMethods={maxHRMethods}
        />

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Heart className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать зоны пульса'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать зоны (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;